import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react'
import { wrap, PanInfo, AnimatePresence, motion } from 'framer-motion'
import { useEffect, ReactElement, useState, useRef } from 'react'
import { useTranslation } from 'next-i18next/pages'
import MainBannerImage from '@/public/assets/images/home/main-banner.jpg'
import MainBannerImageMobile from '@/public/assets/images/home/main-banner-mobile.jpg'
import MainBannerImageDepositBonus from '@/public/assets/images/home/main-banner-deposit-bonus.jpg'
import MainBannerImageDepositBonusMobile from '@/public/assets/images/home/main-banner-deposit-bonus-mobile.jpg'
import { useAppModal, AppModals } from '@/context/ModalProvider'
import { useSession } from '@/context/SessionProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { useEnteringModeValue, EnteringMode } from '@/hooks/useEnteringModeValue'
import { useRouter } from 'next/router'
import { Image } from '@/components/HOC/Image'

const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

const INTERVAL_MS = 15_000

export const Slider = ({ steps }: { steps: Array<ReactElement> }) => {
  const [page, setPage] = useState(0)
  const index = wrap(0, steps.length, page)
  const interval = useRef<NodeJS.Timeout>(null)

  const restartInterval = () => {
    if (interval.current) clearInterval(interval.current)
    interval.current = setInterval(nextSlide, INTERVAL_MS)
  }

  useEffect(() => {
    restartInterval()

    const onVisChange = () => {
      if (document.hidden) {
        !!interval.current && clearInterval(interval.current)
      } else {
        setPage((p) => wrap(0, steps.length, p))
        restartInterval()
      }
    }

    document.addEventListener('visibilitychange', onVisChange)
    return () => {
      !!interval.current && clearInterval(interval.current)
      document.removeEventListener('visibilitychange', onVisChange)
    }
  }, [steps.length])

  const nextSlide = () => {
    setPage((prev) => (prev === steps.length - 1 ? 0 : prev + 1))
  }

  const prevSlide = () => {
    setPage((prev) => (prev === 0 ? steps.length - 1 : prev - 1))
  }

  const handlePaginate = (index: number) => {
    !!interval.current && clearInterval(interval.current)
    setPage(index)
    restartInterval()
  }

  const handlePanEnd = (_event: PointerEvent, info: PanInfo) => {
    !!interval.current && clearInterval(interval.current)
    const swipe = swipePower(info.offset.x, info.velocity.x)

    if (swipe < -swipeConfidenceThreshold) {
      nextSlide()
    } else if (swipe > swipeConfidenceThreshold) {
      prevSlide()
    }
    restartInterval()
  }

  return (
    <Box pos={'absolute'} w={'full'} h={'full'} inset={0}>
      <Box pos={'relative'} h={'full'}>
        <AnimatePresence initial={false}>
          <motion.div
            style={{ position: 'absolute', width: '100%', height: '100%' }}
            key={page}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onPanEnd={handlePanEnd}
          >
            {steps[index]}
          </motion.div>
        </AnimatePresence>
        {steps.length > 1 && (
          <Box
            rounded={'full'}
            bg={'whiteAlpha.300'}
            pos={'absolute'}
            top={'50%'}
            right={2}
            transform={'translateY(-50%)'}
          >
            {steps.map((_, i) => (
              <Box key={i} p={1} pb={0.5} cursor={'pointer'} onClick={() => handlePaginate(i)}>
                <Box
                  border={'1px solid'}
                  w={2}
                  h={2}
                  rounded={'full'}
                  borderColor={'white'}
                  bg={page === i ? 'white' : 'transparent'}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export const BannerProvider = () => {
  const { enteringMode } = useEnteringModeValue()
  const [steps, setSteps] = useState([<MainBannerContent />])

  useEffect(() => {
    if ([EnteringMode.DepositBonus100, EnteringMode.Login].includes(enteringMode)) {
      setSteps([<MainBannerContentPromoDepositBonus />, <MainBannerContent />])
    } else {
      setSteps([<MainBannerContent />])
    }
  }, [enteringMode])

  return <Slider steps={steps} />
}

const MainBannerContentPromoDepositBonus = () => {
  const { t } = useTranslation()
  const modal = useAppModal()
  const router = useRouter()

  const mainBannerCallback = () => {
    const isAuthorized = useSession.getState().account

    if (!isAuthorized) {
      useSession.getState().signIn()
      return
    }

    const hasBalance = Object.entries(useBalanceStore.getState().balance).some(
      ([_, token]) => !!Number(token.amount.humanLocaleString)
    )

    if (!hasBalance) {
      modal.openModalNew(AppModals.DepositModal2)
      return
    }

    router.push('/double')
  }

  return (
    <Flex alignItems={'center'} h={'full'}>
      <Image
        src={MainBannerImageDepositBonus}
        display={{ base: 'none', md: 'block' }}
        pos={'absolute'}
        alt=""
        w={'auto'}
        right={0}
        top={0}
        bottom={0}
      />
      <Image
        src={MainBannerImageDepositBonusMobile}
        display={{ base: 'block', md: 'none' }}
        pos={'absolute'}
        alt=""
        w={'full'}
        inset={0}
      />
      <Box pos={'relative'} zIndex={10} p={{ base: 4, sm: 6, md: 4, xl: 6 }}>
        <Heading
          lineHeight={1.1}
          fontSize={{ base: 'clamp(20px, 6.25vw, 48px)', md: 'clamp(12px, 3.125vw, 48px)' }}
          mb={'clamp(0px, 0.26vw, 4px)'}
          display={'flex'}
          gap={2}
        >
          <Text as={'span'}>{t('Home.deposit-bonus-up-to')} </Text>
          <Text
            as={'span'}
            bg={'#7A4EF8'}
            bgBlendMode={'overlay'}
            bgGradient={`linear-gradient(90deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.25) 25%, rgba(255, 255, 255, 0.5) 33%, rgba(255, 255, 255, 0.25) 66%, rgba(255, 255, 255, 0.25) 75%, rgba(255, 255, 255, 0.25) 100%)`}
            backgroundClip={'text'}
            __css={{
              '-webkit-text-fill-color': 'transparent',
            }}
          >
            {' '}
            100$
          </Text>
        </Heading>
        <Heading
          lineHeight={1.1}
          fontSize={{ base: 'clamp(20px, 6.25vw, 48px)', md: 'clamp(12px, 3.125vw, 48px)' }}
          mb={'clamp(0px, 0.26vw, 4px)'}
          display={'flex'}
          gap={2}
        >
          {t('Home.deposit-bonus')}
        </Heading>
        <Text
          mb={'clamp(0px, 1.3vw, 20px)'}
          fontSize={{ base: 'clamp(12px, 2.1vw, 16px)', md: 'clamp(12px, 1.05vw, 16px)' }}
          color={'ninja.100'}
          fontWeight={500}
          maxW={{ base: '160px', sm: '200px', xl: '250px' }}
        >
          {t('Home.deposit-bonus-description')}{' '}
        </Text>
        <Button
          onClick={() => mainBannerCallback()}
          // as={Link}
          // href={'/double'}
          fontSize={'clamp(12px, 0.91vw, 14px)'}
          px={{ base: 'clamp(16px, 3.38vw, 52px)', md: 'clamp(8px, 3.38vw, 52px)' }}
          h={{ base: 'clamp(42px, 7.82vw, 52px)', md: 'clamp(36px, 3.91vw, 60px)' }}
          rounded={{ base: '2xl', md: 'xl', lg: '2xl' }}
        >
          {t('Home.claim-bonus')}
        </Button>
      </Box>
    </Flex>
  )
}

const MainBannerContent = () => {
  const { t } = useTranslation()
  const modal = useAppModal()
  const router = useRouter()

  const mainBannerCallback = () => {
    const isAuthorized = useSession.getState().account

    if (!isAuthorized) {
      useSession.getState().signIn()
      return
    }

    const hasBalance = Object.entries(useBalanceStore.getState().balance).some(
      ([_, token]) => !!Number(token.amount.humanLocaleString)
    )

    if (!hasBalance) {
      modal.openModalNew(AppModals.DepositModal2)
      return
    }

    router.push('/double')
  }

  return (
    <Flex alignItems={'center'} h={'full'}>
      <Image
        src={MainBannerImage}
        display={{ base: 'none', md: 'block' }}
        pos={'absolute'}
        alt=""
        w={'auto'}
        right={0}
        top={0}
        bottom={0}
      />
      <Image
        src={MainBannerImageMobile}
        display={{ base: 'block', md: 'none' }}
        pos={'absolute'}
        alt=""
        w={'full'}
        inset={0}
      />
      <Box pos={'relative'} zIndex={10} p={{ base: 4, sm: 6, md: 4, xl: 6 }}>
        <Heading
          lineHeight={1.1}
          fontSize={{ base: 'clamp(20px, 6.25vw, 48px)', md: 'clamp(12px, 3.125vw, 48px)' }}
          mb={'clamp(0px, 0.26vw, 4px)'}
        >
          {t('Home.welcome-title')}
        </Heading>
        <Text
          mb={'clamp(0px, 1.3vw, 20px)'}
          fontSize={{ base: 'clamp(12px, 2.1vw, 16px)', md: 'clamp(12px, 1.05vw, 16px)' }}
          color={'ninja.100'}
          fontWeight={500}
          maxW={{ base: '200px', xl: '250px' }}
        >
          {t('Home.platform-description')}{' '}
        </Text>
        <Button
          onClick={() => mainBannerCallback()}
          // as={Link}
          // href={'/double'}
          fontSize={'clamp(12px, 0.91vw, 14px)'}
          px={{ base: 'clamp(16px, 3.38vw, 52px)', md: 'clamp(8px, 3.38vw, 52px)' }}
          h={{ base: 'clamp(42px, 7.82vw, 52px)', md: 'clamp(36px, 3.91vw, 60px)' }}
          rounded={{ base: '2xl', md: 'xl', lg: '2xl' }}
        >
          {t('common.play-now')}
        </Button>
      </Box>
    </Flex>
  )
}
